import type { ObjectDirective } from 'vue'

const CHUNK_SIZE = 2048
const states = new WeakMap<HTMLElement, { text: string; tail: Text | null }>()

function update(element: HTMLElement, text: string) {
  let state = states.get(element)
  if (!state) {
    state = { text: '', tail: null }
    states.set(element, state)
  }
  if (text === state.text) return
  // Retries, edits, or saved replacements can change more than the suffix.
  if (!text.startsWith(state.text)) {
    element.textContent = ''
    state.text = ''
    state.tail = null
  }
  let position = state.text.length
  while (position < text.length) {
    const room = CHUNK_SIZE - (state.tail?.length ?? 0)
    if (!state.tail || room < 2) {
      state.tail = element.ownerDocument.createTextNode('')
      element.appendChild(state.tail)
    }
    let end = Math.min(text.length, position + CHUNK_SIZE - state.tail.length)
    // Keep a surrogate pair together when it straddles a chunk boundary.
    if (end < text.length && /[\uD800-\uDBFF]/u.test(text[end - 1])) end--
    state.tail.appendData(text.slice(position, end))
    position = end
  }
  state.text = text
}

/** Owns text children of a plain-text element; never interprets output as HTML. */
export const vStreamingText: ObjectDirective<HTMLElement, string> = {
  mounted: (element, binding) => update(element, binding.value),
  updated: (element, binding) => update(element, binding.value),
  beforeUnmount: (element) => { states.delete(element) },
}
